import axios, { AxiosHeaders, AxiosInstance, AxiosRequestHeaders, AxiosResponse, CreateAxiosDefaults } from 'axios';
import React from 'react';
import { SSE } from 'sse.js';

// 添加 ImportMeta 接口声明
declare global {
  interface ImportMeta {
    env: {
      DEV: boolean;
      VITE_CANVAS_API_BASE_URL: string;
      VITE_OGS_API_BASE_URL: string;
      // VITE_USE_NEW_LOGIN_API?: string;
    };
  }
}

export interface PageData<T = unknown> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface PageQuery {
  page?: number;
  size?: number;
  order_by?: string;
  resource_type?: 'robot' | 'dataset';
}

export const PageQuery = {
  toString: (query: PageQuery) => {
    return Object.keys(query)
      .map((key) => `${key}=${query[key as keyof PageQuery]}`)
      .join('&');
  },
};

export interface HttpInstance extends AxiosInstance {
  fetch: (input: RequestInfo | URL, init?: RequestInit & { params?: unknown }) => ReturnType<typeof fetch>;
}

interface SingletonInterface {
  http: HttpInstance | null;
  setHttp: (http: HttpInstance) => void;
}

const singleton: SingletonInterface = {
  http: null,
  setHttp: (http) => {
    singleton.http = http;
  },
};

export const Http = {
  init: (config?: CreateAxiosDefaults) => {
    const axiosInstance = axios.create({
      timeout: 5 * 60 * 1000,
      ...config,
    });

    const prevInterceptorsRequestUse = axiosInstance.interceptors.request.use;
    const requestUseFns: Parameters<typeof axiosInstance.interceptors.request.use>[0][] = [];
    axiosInstance.interceptors.request.use = (fn) => {
      requestUseFns.push(fn);
      return prevInterceptorsRequestUse.bind(axiosInstance.interceptors.request)(function (config) {
        if (!fn) return config;
        return fn(config);
      });
    };

    const afterInterceptorsResponseUse = axiosInstance.interceptors.response.use;
    const responseErrorFns: Parameters<typeof axiosInstance.interceptors.response.use>[0][] = [];
    axiosInstance.interceptors.response.use = (fn, errorFn) => {
      responseErrorFns.push(errorFn);
      return afterInterceptorsResponseUse.bind(axiosInstance.interceptors.response)(
        function (response) {
          if (!fn) return response;
          return fn(response);
        },
        function (response) {
          if (!errorFn) return response;
          return errorFn(response);
        }
      );
    };

    const instance = {
      ...axiosInstance,
      fetch: (input, init) => {
        const axiosHeaders = new AxiosHeaders();
        const initHeaders = init?.headers ?? {};
        const headers = { ...axiosHeaders, ...initHeaders } as AxiosRequestHeaders;

        requestUseFns.forEach((fn) => {
          fn?.({ headers });
        });
        if (typeof input === 'string') {
          if (input.startsWith('/')) {
            const { baseURL = '/' } = axiosInstance.defaults;
            input = (baseURL.startsWith('/') ? window.location.origin + baseURL : baseURL) + input;
          }

          if (init?.params) {
            const searchParams = new URLSearchParams();
            for (const key in init.params) {
              const value = init.params[key as keyof typeof init.params];
              if (!value) continue;
              searchParams.set(key, String(value));
            }
            input = input + (input.indexOf('?') > -1 ? '&' : '?') + searchParams;
          }
        }
        return fetch(input, { ...init, headers }).then((response) => {
          console.log('error ', response, responseErrorFns);
          if (!response.ok) {
            const errorFun = responseErrorFns[0];
            if (errorFun) {
              return errorFun({ response } as unknown as AxiosResponse);
            }
            return response;
          } else {
            return response;
          }
        });
      },
    } as HttpInstance;

    singleton.setHttp(instance);
    return instance;
  },
  use: () => {
    const { http } = singleton;
    if (!http) {
      throw new Error('Please init Http first');
    }
    return http;
  },
  createEventSource: (
    url: string,
    options?: { headers: Record<string, string>; autoReconnect: boolean; reconnectDelay: number; maxRetries: number }
  ) => {
    const { http } = singleton;
    if (!http) {
      throw new Error('Please init Http first');
    }
    if (url.startsWith('/')) {
      const { baseURL = '/' } = http.defaults;
      url = (baseURL.startsWith('/') ? window.location.origin + baseURL : baseURL) + url;
    }
    return new SSE(url, options);
  },
};

export const useHttp = () => {
  const [http, setHttp] = React.useState<HttpInstance | null>(singleton.http);
  React.useLayoutEffect(() => {
    const prev = singleton.setHttp;
    singleton.setHttp = (http) => {
      setHttp(http);
      prev(http);
    };
  }, []);
  return http;
};
